import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

export function PageHeader({ label, title, subtitle, children }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 fade-up">
      <div>
        {label && <div className="lx-label mb-2">{label}</div>}
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-slate-400 mt-2 max-w-2xl">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export function Kpi({ label, value, icon: Icon, accent = "text-emerald-400", hint, testid }) {
  return (
    <div className="lx-card fade-up" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        {Icon && <Icon className={`h-4 w-4 ${accent}`} />}
      </div>
      <div className="mt-3 text-xl sm:text-2xl font-bold tracking-tight font-mono break-words">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

const TONES = {
  green: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  cyan: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
  amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  rose: "bg-rose-500/10 text-rose-400 border-rose-500/20",
  slate: "bg-slate-700/30 text-slate-300 border-slate-600/40",
  violet: "bg-violet-500/10 text-violet-400 border-violet-500/20",
};
const STATUS_TONE = {
  Active: "green", Selling: "green", Launched: "green", Booked: "green", Confirmed: "green", Completed: "green", Converted: "green", Approved: "green", completed: "green", active: "green", paid: "green", Available: "cyan",
  Paused: "amber", Reserved: "amber", Pending: "amber", pending: "amber", success: "green", refunded: "violet", Scheduled: "cyan", processing: "cyan", trial: "cyan", "Pre-Launch": "violet", Negotiation: "amber",
  Lost: "rose", Cancelled: "rose", "No-Show": "rose", failed: "rose", suspended: "rose", expired: "rose", Archived: "slate", Draft: "slate",
};

export function Badge2({ children, tone }) {
  const t = TONES[tone || STATUS_TONE[children] || "slate"];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${t}`}>{children}</span>;
}

export const Mocked = ({ what = "MOCKED" }) => <span className="lx-mock-badge" data-testid="mocked-badge">{what}</span>;

export function useProjects(onLoaded) {
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get("/projects").then((r) => { setProjects(r.data); onLoaded?.(r.data); }).catch(() => onLoaded?.(null)); }, []); // eslint-disable-line
  return projects;
}

export function ProjectSelect({ value, onChange, allowAll, testid = "project-select", className = "w-56", onLoaded }) {
  const projects = useProjects(onLoaded);
  useEffect(() => {
    if (!allowAll && !value && projects.length) onChange(projects.find((p) => p.status !== "Archived")?.id || projects[0].id);
  }, [projects]); // eslint-disable-line
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger className={className} data-testid={testid}><SelectValue placeholder="Select project" /></SelectTrigger>
      <SelectContent>
        {allowAll && <SelectItem value="all">All projects</SelectItem>}
        {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function Empty({ title, children }) {
  return (
    <div className="lx-card text-center py-14 lx-grid-bg" data-testid="empty-state">
      <div className="font-semibold">{title}</div>
      <div className="text-sm text-slate-400 mt-1">{children}</div>
    </div>
  );
}

export const CHART = { revenue: "#0B5D36", leads: "#C9A227", inventory: "#2F855A", bookings: "#E0B84A", cost: "#B4413C" };
export const PALETTE = ["#0B5D36", "#C9A227", "#2F855A", "#E0B84A", "#68A882", "#8A6A0C", "#B4413C", "#4A5568"];
export const tooltipStyle = { contentStyle: { background: "#ffffff", border: "1px solid #e6eaf0", borderRadius: 8, fontSize: 12, boxShadow: "0 4px 12px rgba(15,23,42,0.08)" }, labelStyle: { color: "#0f172a" } };
export const GRID = "#e6eaf0";
